/**
 * MagicPosterPanel 常量配置
 */
import React from 'react';
import classNames from 'classnames';
import { Bubble } from 'tea-component';
import { TReportStepType, IStepTabConfig, IToolbarActionItem, IExportFileTypeItem } from '../../../components/ReportViewer/types';
import { EPosterActions, EPosterExportType } from './types';
import defaultRefreshIcon from '@/pages/aigc/assets/svg/agent-ds-refresh.svg';
import defaultExportIcon from '@/pages/aigc/assets/svg/agent-ds-export.svg';
import shareIcon from '@/pages/aigc/assets/svg/icon-share.svg';
import iconSmartAi from '@/pages/aigc/assets/svg/icon-smart-ai.svg';
// 导出下拉列表图标
import exportPngIcon from '@/pages/aigc/assets/svg/icon-export-png.svg';
import exportJpgIcon from '@/pages/aigc/assets/svg/icon-export-jpg.svg';

/** 文件后缀类型常量 */
export const POSTER_FILE_SUFFIX = {
  /** 内容规划（Markdown） */
  MD: 'md_file' as const,
  /** 海报预览（PNG） */
  PNG: 'png_file' as const,
};

/** 文件类型常量（接口请求 file_type 参数） */
export const POSTER_FILE_TYPE = 'magic_poster' as const;

/** 海报步骤类型常量 */
export const POSTER_STEP_TYPE = {
  FRAMEWORK: 'framework' as TReportStepType,
  POSTER: 'report' as TReportStepType, // 复用 'report' 类型值，映射为海报预览步骤
};

/** 海报步骤页签配置 */
export const POSTER_STEP_TABS_CONFIG: IStepTabConfig[] = [
  {
    label: '内容规划',
    labelEn: 'Content Planning',
    value: POSTER_STEP_TYPE.FRAMEWORK,
  },
  {
    label: '魔法海报预览',
    labelEn: 'Magic Poster Preview',
    value: POSTER_STEP_TYPE.POSTER,
  },
];

/** 海报导出文件类型配置 */
export const POSTER_EXPORT_FILE_TYPE_CONFIG: IExportFileTypeItem[] = [
  { label: 'PNG', value: EPosterExportType.PNG, labelEn: 'PNG', icon: exportPngIcon },
  { label: 'JPG', value: EPosterExportType.JPG, labelEn: 'JPG', icon: exportJpgIcon },
];

/** 获取海报工具栏操作列表 */
export const getPosterToolbarActions = (
  isEn: boolean,
  streamingState: string,
  styleValid: boolean,
  posterLoading = false,
): IToolbarActionItem[] => {
  // 流式状态中判定
  const isStreaming = !!streamingState && streamingState !== 'complete';
  
  return [
    // ========== Step1 (framework) 操作 ==========
    {
      label: (
        <div className="dba-report-viewer-toolbar-action">
          <img src={defaultRefreshIcon} alt="" />
          {isEn ? 'Reset' : '重置'}
        </div>
      ),
      value: POSTER_STEP_TYPE.FRAMEWORK,
      key: EPosterActions.FrameworkReset,
    },
    {
      label: (
        <Bubble
          content={isEn ? '3 high-quality generations per day. Standard mode after.' : '每日3次高质量生成，之后为标准模式'}
          placement="top"
        >
          <div
            className={classNames(
              'dba-report-viewer-toolbar-action dba-report-viewer-toolbar-action--primary',
              {
                'dba-report-viewer-toolbar-action--disabled':
                  !styleValid || isStreaming,
              }
            )}
          >
            <img src={iconSmartAi} alt="" />
            {isEn ? 'Generate Magic Poster' : '生成魔法海报'}
          </div>
        </Bubble>
      ),
      value: POSTER_STEP_TYPE.FRAMEWORK,
      key: EPosterActions.GeneratePoster,
    },
    // ========== Step2 (poster/report) 操作 ==========
    {
      label: (
        <div className={classNames(
          'dba-report-viewer-toolbar-action',
          {
            'dba-report-viewer-toolbar-action--disabled-no-bg': isStreaming || posterLoading,
          }
        )}>
          <img src={defaultRefreshIcon} alt="" />
          {isEn ? 'Regenerate' : '重新生成'}
        </div>
      ),
      value: POSTER_STEP_TYPE.POSTER,
      key: EPosterActions.Regenerate,
    },
    {
      label: (
        <div className={classNames(
          'dba-report-viewer-toolbar-action',
          {
            'dba-report-viewer-toolbar-action--disabled-no-bg': posterLoading,
          }
        )}>
          <img src={defaultExportIcon} alt="" />
          {isEn ? 'Export' : '导出'}
        </div>
      ),
      value: POSTER_STEP_TYPE.POSTER,
      key: EPosterActions.Export,
    },
    {
      label: (
        <div className={classNames(
          'dba-report-viewer-toolbar-action dba-report-viewer-toolbar-action--primary',
          {
            'dba-report-viewer-toolbar-action--disabled': posterLoading,
          }
        )}>
          <img src={shareIcon} alt="" />
          {isEn ? 'Share' : '分享'}
        </div>
      ),
      value: POSTER_STEP_TYPE.POSTER,
      key: EPosterActions.Share,
    },
  ];
};